/**
 * RC 充值/提现共用的链与代币映射（与 DepositDialog.buildChainsForSymbol 对齐）。
 * chainCode / tokenId 必须来自 /rc/tokens + /rc/chains，或与 /rc/balances 的 tokenId 对齐。
 * 禁止前端拼接 chainCode（如 MATIC_POLYGON），须与后端 / Cobo 配置一致。
 */

const CHAIN_ALIAS_TO_NUMERIC = {
  MATIC: '137',
  POLYGON: '137',
  ETH: '1',
  ETHEREUM: '1',
  ARB: '42161',
  ARBITRUM: '42161',
};

/**
 * chainCode 仅取自 API 字段；无 chainCode 时用 chainId（与 DepositDialog 一致）。
 * 不用 tokenId 前缀或 symbol/nativeToken 拼接。
 */
export function resolveRcChainCode(chainItem, tokenItem) {
  const fromToken = String(tokenItem?.chainCode ?? '').trim();
  if (fromToken) return fromToken;

  const fromChain = String(chainItem?.chainCode ?? '').trim();
  if (fromChain) return fromChain;

  return String(chainItem?.chainId ?? '').trim();
}

export function normalizeRcChainKey(chainId) {
  const raw = String(chainId ?? '').trim();
  if (!raw) return '';
  if (/^\d+$/.test(raw)) return raw;
  return CHAIN_ALIAS_TO_NUMERIC[raw.toUpperCase()] ?? raw;
}

export function mapRcChain(chainItem, tokenItem) {
  const chainId = String(chainItem?.chainId ?? '').trim();
  const chainCode = resolveRcChainCode(chainItem, tokenItem);
  return {
    id: chainId,
    chainId,
    chainCode,
    tokenId: String(tokenItem?.tokenId ?? '').trim(),
    contractAddress: tokenItem?.contractAddress ?? '',
    decimals: tokenItem?.decimals,
    label: chainItem?.symbol || chainItem?.nativeToken || chainCode,
  };
}

export function findRcTokenById(tokens, tokenId) {
  const tid = String(tokenId ?? '').trim();
  if (!tid) return null;
  return (
    (tokens || []).find(
      (item) => String(item?.tokenId ?? '').trim() === tid && item?.enabled !== false
    ) ?? null
  );
}

/**
 * 按平台余额 tokenId 解析提现/充值参数（与 /rc/balances、/rc/balance/{tokenId} 一致）
 */
export function resolveRcChainParamsForTokenId(tokens, chains, tokenId) {
  const token = findRcTokenById(tokens, tokenId);
  if (!token) return null;

  const tokenChainKey = normalizeRcChainKey(token.chainId);
  const chain = (chains || []).find((item) => {
    if (item?.enabled === false) return false;
    const key = normalizeRcChainKey(item?.chainId);
    return key && tokenChainKey && key === tokenChainKey;
  });
  if (!chain) return null;

  return mapRcChain(chain, token);
}

/**
 * @param {Array} tokens getRcTokens 返回值
 * @param {Array} chains getRcChains 返回值
 * @param {string} symbol 如 USDC
 */
export function buildRcChainsForSymbol(tokens, chains, symbol) {
  const sym = String(symbol ?? '')
    .trim()
    .toUpperCase();
  if (!sym) return [];

  const tokenByChain = new Map();
  for (const item of tokens || []) {
    if (
      String(item?.symbol ?? '')
        .trim()
        .toUpperCase() !== sym
    )
      continue;
    if (item?.enabled === false) continue;
    const chainKey = normalizeRcChainKey(item?.chainId);
    if (chainKey) tokenByChain.set(chainKey, item);
  }

  return (chains || [])
    .filter((chain) => {
      const chainKey = normalizeRcChainKey(chain?.chainId);
      return chain?.enabled !== false && chainKey && tokenByChain.has(chainKey);
    })
    .map((chain) => mapRcChain(chain, tokenByChain.get(normalizeRcChainKey(chain.chainId))))
    .filter((chain) => chain.chainCode && chain.tokenId);
}

/**
 * 提现/充值默认链：优先平台余额 tokenId，其次配置链（137 等）
 */
export function pickRcChainForContext(tokens, chains, options = {}) {
  const sym = String(options.symbol ?? 'USDC')
    .trim()
    .toUpperCase();
  const platformTokenId = String(options.platformTokenId ?? '').trim();
  if (platformTokenId) {
    const fromBalance = resolveRcChainParamsForTokenId(tokens, chains, platformTokenId);
    if (fromBalance) return fromBalance;
  }

  const rows = buildRcChainsForSymbol(tokens, chains, sym);
  if (!rows.length) return null;

  const preferred = String(options.preferredChainId ?? '').trim();
  if (preferred) {
    const preferredKey = normalizeRcChainKey(preferred);
    const onPreferred = rows.find((row) => normalizeRcChainKey(row.chainId) === preferredKey);
    if (onPreferred) return onPreferred;
  }

  return rows[0];
}
